import { useEffect, useState } from 'react';
import { apiRequest } from '../utils/auth';

export interface AnalyticsResource<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

/** A response belongs to its exact scope, including while the next effect is pending. */
export function useAnalyticsResource<T>(path: string | null, refresh: number): AnalyticsResource<T> {
  const key = path ? `${path}:${refresh}` : null;
  const [state, setState] = useState<AnalyticsResource<T> & { key: string | null }>({ key: null, data: null, loading: false, error: null });
  useEffect(() => {
    if (!path) {
      setState({ key: null, data: null, loading: false, error: null });
      return;
    }
    const controller = new AbortController();
    setState({ key, data: null, loading: true, error: null });
    apiRequest<T>(path, { signal: controller.signal })
      .then((data) => {
        if ((data as { success?: boolean }).success === false) throw new Error('The report service did not return a successful response.');
        if (!controller.signal.aborted) setState({ key, data, loading: false, error: null });
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) setState({ key, data: null, loading: false, error: error instanceof Error ? error.message : 'Unable to load this report.' });
      });
    return () => controller.abort();
  }, [path, key]);
  return path && state.key === key
    ? { data: state.data, loading: state.loading, error: state.error }
    : { data: null, loading: Boolean(path), error: null };
}
